"use client"

import type { VariantProps } from "class-variance-authority"
import type { FC, SVGProps } from "react"
import * as React from "react"
import { cva } from "class-variance-authority"

import { cn } from "@productlane/lib/src/browser"

import type { IconType } from "../icons"
import type { ButtonProps } from "./button"
import { sharedVariantStyles } from "./button"

export const iconButtonVariants = cva(
	"relative inline-flex h-fit flex-none rounded-full select-none items-center justify-center align-middle focus:outline-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 active:opacity-80 disabled:pointer-events-none",
	{
		variants: {
			variant: {
				...sharedVariantStyles,
				solid:
					"bg-gray-900 text-white hover:bg-gray-700 dark:bg-gray-100 dark:text-gray-900 dark:hover:bg-gray-300",
				"ghost-brightness":
					"bg-transparent data-[state=open]:bg-transparent text-secondary hover:text-primary hover:brightness-75 dark:hover:brightness-150 dark:data-[state=open]:bg-transparent",
				"dark-ghost":
					"bg-transparent hover:bg-gray-200 data-[state=open]:bg-transparent text-secondary hover:text-primary dark:hover:bg-gray-700  dark:data-[state=open]:bg-transparent",
				// The add button after a row of reaction chips: no fill until hovered, then the chip's hover fill.
				"reaction-add":
					"bg-transparent text-secondary hover:bg-black/[0.08] hover:text-primary data-[state=open]:bg-black/[0.08] data-[state=open]:text-primary dark:text-gray-400 dark:hover:bg-white/10 dark:hover:text-gray-200 dark:data-[state=open]:bg-white/10 dark:data-[state=open]:text-gray-200",
			},
			size: {
				sm: "h-5 w-5 rounded-full",
				md: "h-6 w-6 rounded-full",
				default: "h-7 w-7",
				lg: "h-10 w-10",
				xl: "h-12 w-12",
			},
			isRounded: {
				true: "rounded-full",
			},
		},
		defaultVariants: {
			variant: "outline",
			size: "default",
		},
	},
)

export type iconButtonVariantsType =
	| "solid"
	| "outline"
	| "subtle"
	| "ghost"
	| "ghost-brightness"
	| "portal"
	| "dark-ghost"
	| "reaction-add"
	| "glass"
	| "glassPrimary"

export interface IconButtonProps
	extends
		React.ButtonHTMLAttributes<HTMLButtonElement>,
		VariantProps<typeof iconButtonVariants>,
		Pick<ButtonProps, "badge" | "badgePosition"> {
	Icon?: LucideIcon | IconType
	iconClassName?: string
	iconStrokeWidth?: number
	children?: React.ReactNode
	badgeClassName?: string
}

export const IconButton = React.memo(
	React.forwardRef<HTMLButtonElement, IconButtonProps>(
		(
			{
				className,
				variant,
				isRounded,
				size,
				badge,
				badgePosition,
				type = "button",
				children,
				Icon,
				iconClassName,
				iconStrokeWidth = 1.75,
				badgeClassName,
				...props
			},
			ref,
		) => {
			return (
				<button
					className={cn(
						iconButtonVariants({ variant, size, isRounded }),
						className,
					)}
					ref={ref}
					type={type}
					{...props}
				>
					{badge ? (
						<div
							className={cn(
								"absolute min-w-3.5 rounded-md bg-gray-200/80 px-1 py-0.5 text-[10px] font-medium leading-none text-gray-600 dark:bg-gray-850 dark:text-gray-400",
								{
									"-left-1 -top-1": badgePosition === "top-left",
									"-right-1 -top-1": badgePosition === "top-right",
									"-bottom-1 -left-1": badgePosition === "bottom-left",
									"-bottom-1 -right-1": badgePosition === "bottom-right",
								},
								badgeClassName,
								{
									"!right-0":
										badge.toLocaleString().length === 1 &&
										badgePosition?.includes("right"),
									"!right-1":
										badge.toLocaleString().length === 2 &&
										badgePosition?.includes("right"),
									"!right-2.5 !px-0.5":
										badge.toLocaleString().length >= 3 &&
										badgePosition?.includes("right"),
									"!left-0":
										badge.toLocaleString().length === 1 &&
										badgePosition?.includes("left"),
									"!left-1":
										badge.toLocaleString().length === 2 &&
										badgePosition?.includes("left"),
									"!left-2.5 !px-0.5":
										badge.toLocaleString().length >= 3 &&
										badgePosition?.includes("left"),
								},
							)}
						>
							{badge}
						</div>
					) : null}
					{children ? children : null}
					{Icon ? (
						<Icon
							strokeWidth={iconStrokeWidth}
							className={cn("h-3.5 w-3.5 shrink-0", iconClassName, {
								"text-gray-200 dark:text-gray-900": variant === "glassPrimary",
								"text-tertiary dark:text-inherit":
									variant === "outline" ||
									variant === "ghost" ||
									variant === "ghost-brightness" ||
									variant === "dark-ghost" ||
									variant === "editor" ||
									variant === "dashed" ||
									variant === "glass" ||
									variant === "link" ||
									variant === "link-no-hover",
								"text-secondary dark:text-inherit": variant === "subtle",
							})}
						/>
					) : null}
				</button>
			)
		},
	),
)

type LucideIcon = FC<SVGProps<SVGSVGElement>>
